import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

import paths from '@src/paths';
import { toast } from '@src/components/core/toaster';
import defaultClient from '@src/api/clients/default';

const STATUS_COLORS = {
  new_business: 'info',
  onboarding: 'warning',
  active_new: 'success',
  active: 'success',
  paused: 'default',
  off: 'error',
};

const STATUS_LABELS = {
  new_business: 'New',
  onboarding: 'Onboarding',
  active_new: 'Active (New)',
  active: 'Active',
  paused: 'Paused',
  off: 'Off',
};

export default function OnboardingDashboardPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const res = await defaultClient.request({ url: `tasks/onboarding-tasks/dashboard/${params}` });
      setClients(res?.data?.results || []);
    } catch {
      toast.error('Failed to load onboarding dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, [statusFilter]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Onboarding Dashboard</Typography>
        <Stack direction="row" spacing={2}>
          <TextField
            select size="small" label="Status" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}
          >
            <MenuItem value="">All Active</MenuItem>
            <MenuItem value="new_business">New Business</MenuItem>
            <MenuItem value="onboarding">Onboarding</MenuItem>
          </TextField>
          <Button startIcon={<RefreshIcon />} onClick={loadData} size="small">Refresh</Button>
        </Stack>
      </Stack>

      <Card>
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={32} /></Box>
          ) : clients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">No clients in onboarding.</Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, py: 1, px: 1.5, whiteSpace: 'nowrap' } }}>
                  <TableCell>Client</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Build Progress</TableCell>
                  <TableCell>Current Step</TableCell>
                  <TableCell>Waiting On</TableCell>
                  <TableCell>Launch</TableCell>
                  <TableCell sx={{ width: 40 }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clients.map((c) => {
                  const hasWaiting = c.waiting_on_client?.length > 0;
                  return (
                    <TableRow key={c.client_id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(paths.onboarding.internal(c.client_slug))}
                      sx={{ '& td': { py: 1, px: 1.5, fontSize: 13 } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ fontSize: 13 }}>{c.client_name}</Typography>
                        <Chip label={STATUS_LABELS[c.client_status] || c.client_status} size="small"
                          color={STATUS_COLORS[c.client_status] || 'default'} sx={{ fontSize: 10, height: 18, mt: 0.25 }} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          <LinearProgress variant="determinate" value={c.progress}
                            sx={{ flex: 1, height: 5, borderRadius: 3 }}
                            color={c.progress === 100 ? 'success' : 'primary'} />
                          <Typography variant="caption" fontWeight={600} sx={{ fontSize: 11 }}>{c.progress}%</Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                          {c.completed_tasks}/{c.total_tasks}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {c.current_phase ? (
                          <Chip label={c.current_phase} size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20, fontWeight: 600 }} />
                        ) : (
                          <Chip label="Done" size="small" color="success" sx={{ fontSize: 10, height: 20 }} />
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const waiting = [...(c.waiting_on_client || [])];
                          if ((c.onboarding_progress || 0) < 100) waiting.unshift('Onboarding');
                          return waiting.length > 0 ? (
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {waiting.map((w) => (
                                <Chip key={w} label={w} size="small" color="warning" variant="outlined" sx={{ fontSize: 9, height: 18 }} />
                              ))}
                            </Stack>
                          ) : (
                            <CheckCircleIcon sx={{ color: 'success.main', fontSize: 18 }} />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600} sx={{ color: '#ff680d', fontSize: 11, whiteSpace: 'nowrap' }}>
                          {c.estimated_launch ? new Date(c.estimated_launch + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.5}>
                          <Button component={Link} to={paths.onboarding.internal(c.client_slug)}
                            size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Staff</Button>
                          <Button component="a" href={`/onboarding/${c.client_slug}`} target="_blank"
                            size="small" sx={{ fontSize: 11, minWidth: 0, px: 1 }}>Client</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
